import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Save, Sparkles, AlertCircle, Eye } from "lucide-react";
import { adminApi, formatApiErrorDetail } from "../../lib/adminApi";

const CATEGORIES = ["Performans", "SEO", "Sosyal Medya", "Kreatif", "Strateji"];

function slugify(s) {
  return (s || "").toLowerCase()
    .replaceAll("ı", "i").replaceAll("ş", "s").replaceAll("ğ", "g")
    .replaceAll("ü", "u").replaceAll("ö", "o").replaceAll("ç", "c")
    .replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 80);
}

export default function AdminBlogEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;

  const [form, setForm] = useState({
    slug: "", category: "Performans", tags: "", status: "draft",
    cover_image_url: "", reading_time_min: 8,
    title: "", excerpt: "", content: "", seo_title: "", seo_description: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [translateBusy, setTranslateBusy] = useState(false);

  useEffect(() => {
    if (isNew) return;
    adminApi.get(`/admin/articles/${id}`).then((r) => {
      const a = r.data;
      const tr = a.translations?.tr || {};
      setForm({
        slug: a.slug || "",
        category: a.category || "Performans",
        tags: (a.tags || []).join(", "),
        status: a.status || "draft",
        cover_image_url: a.cover_image_url || "",
        reading_time_min: a.reading_time_min || 8,
        title: tr.title || "", excerpt: tr.excerpt || "",
        content: tr.content || "", seo_title: tr.seo_title || "",
        seo_description: tr.seo_description || "",
      });
    }).catch((e) => setErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message));
  }, [id, isNew]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const onTitleBlur = () => {
    if (!form.slug) setForm((f) => ({ ...f, slug: slugify(f.title) }));
  };

  const save = async (publish) => {
    setBusy(true);
    setErr("");
    const payload = {
      slug: form.slug || slugify(form.title),
      category: form.category,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
      status: publish ? "published" : form.status,
      cover_image_url: form.cover_image_url,
      reading_time_min: Number(form.reading_time_min) || 8,
      translations: {
        tr: {
          title: form.title, excerpt: form.excerpt, content: form.content,
          seo_title: form.seo_title || form.title,
          seo_description: form.seo_description || form.excerpt,
        },
      },
    };
    try {
      if (isNew) {
        const { data } = await adminApi.post("/admin/articles", payload);
        navigate(`/admin/blog/${data.id}/edit`, { replace: true });
      } else {
        await adminApi.put(`/admin/articles/${id}`, payload);
      }
      navigate("/admin/blog");
    } catch (e) {
      setErr(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const translate = async () => {
    if (!id) { alert("Önce yazıyı kaydet."); return; }
    setTranslateBusy(true);
    try {
      await adminApi.post(`/admin/articles/${id}/translate`);
      alert("Çeviri arka planda başlatıldı. Birkaç dakika sürebilir.");
    } catch (e) {
      alert(formatApiErrorDetail(e?.response?.data?.detail) || e.message);
    } finally { setTranslateBusy(false); }
  };

  return (
    <div data-testid="admin-blog-editor-page" className="space-y-6 max-w-4xl">
      <Helmet><title>{isNew ? "Yeni yazı" : "Yazı düzenle"} · Admin</title></Helmet>

      <Link to="/admin/blog" className="inline-flex items-center gap-2 text-sm text-ink/60 hover:text-ink">
        <ArrowLeft className="h-4 w-4" /> Tüm yazılar
      </Link>

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h1 className="font-display text-h1 font-bold text-ink">
          {isNew ? "Yeni yazı" : "Yazıyı düzenle"}
        </h1>
        <div className="flex items-center gap-2">
          {!isNew && (
            <button onClick={translate} disabled={translateBusy} data-testid="admin-editor-translate"
              className="inline-flex items-center gap-2 rounded-full border-2 border-mist px-4 py-2 text-sm hover:border-brand-pink disabled:opacity-60">
              <Sparkles className="h-4 w-4" />
              {translateBusy ? "Çevriliyor…" : "AI çevir (5 dil)"}
            </button>
          )}
          {!isNew && (
            <a href={`/tr/blog/${form.slug}`} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full border-2 border-mist px-4 py-2 text-sm hover:border-brand-pink">
              <Eye className="h-4 w-4" /> Önizle
            </a>
          )}
        </div>
      </div>

      {err ? <div data-testid="admin-editor-error" className="flex items-start gap-2 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700"><AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />{err}</div> : null}

      <div className="rounded-2xl bg-white border border-mist p-6 sm:p-7 space-y-5">
        <div>
          <label className="text-xs uppercase tracking-[0.18em] text-ink/55 font-bold">Başlık (TR)</label>
          <input value={form.title} onChange={set("title")} onBlur={onTitleBlur} data-testid="ed-title" required
            className="mt-1.5 w-full rounded-xl border-2 border-mist bg-cream/50 px-4 py-3 focus:border-brand-pink focus:bg-white outline-none text-ink font-display font-semibold text-lg" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-ink/55 font-bold">Slug</label>
            <input value={form.slug} onChange={set("slug")} data-testid="ed-slug"
              className="mt-1.5 w-full rounded-xl border-2 border-mist bg-cream/50 px-4 py-2.5 focus:border-brand-pink focus:bg-white outline-none text-sm font-mono" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-ink/55 font-bold">Kategori</label>
            <select value={form.category} onChange={set("category")} data-testid="ed-category"
              className="mt-1.5 w-full rounded-xl border-2 border-mist bg-cream/50 px-4 py-2.5 focus:border-brand-pink focus:bg-white outline-none text-sm">
              {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-ink/55 font-bold">Etiketler (virgülle)</label>
            <input value={form.tags} onChange={set("tags")} data-testid="ed-tags"
              className="mt-1.5 w-full rounded-xl border-2 border-mist bg-cream/50 px-4 py-2.5 focus:border-brand-pink focus:bg-white outline-none text-sm" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.18em] text-ink/55 font-bold">Cover image URL</label>
            <input value={form.cover_image_url} onChange={set("cover_image_url")} data-testid="ed-cover"
              className="mt-1.5 w-full rounded-xl border-2 border-mist bg-cream/50 px-4 py-2.5 focus:border-brand-pink focus:bg-white outline-none text-sm font-mono" />
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.18em] text-ink/55 font-bold">Özet (excerpt)</label>
          <textarea value={form.excerpt} onChange={set("excerpt")} data-testid="ed-excerpt" rows={3}
            className="mt-1.5 w-full rounded-xl border-2 border-mist bg-cream/50 px-4 py-3 focus:border-brand-pink focus:bg-white outline-none text-sm" />
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.18em] text-ink/55 font-bold">İçerik (Markdown)</label>
          <textarea value={form.content} onChange={set("content")} data-testid="ed-content" rows={20}
            className="mt-1.5 w-full rounded-xl border-2 border-mist bg-cream/50 px-4 py-3 focus:border-brand-pink focus:bg-white outline-none text-sm font-mono leading-relaxed" />
          <p className="mt-1 text-xs text-ink/50"># Başlık · ## Alt başlık · **kalın** · *italik* · [link](url) · - liste · {">"} alıntı · ```kod```</p>
        </div>
        <details className="rounded-xl border border-mist bg-mist/40 p-4">
          <summary className="cursor-pointer text-sm font-semibold text-ink/80">SEO Meta</summary>
          <div className="mt-4 space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-ink/55 font-bold">SEO Title (60 kar.)</label>
              <input value={form.seo_title} onChange={set("seo_title")} maxLength={70} data-testid="ed-seotitle"
                className="mt-1.5 w-full rounded-xl border-2 border-mist bg-white px-4 py-2.5 outline-none text-sm" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.18em] text-ink/55 font-bold">Meta Description (160 kar.)</label>
              <textarea value={form.seo_description} onChange={set("seo_description")} maxLength={170} rows={2} data-testid="ed-seodesc"
                className="mt-1.5 w-full rounded-xl border-2 border-mist bg-white px-4 py-2.5 outline-none text-sm" />
            </div>
          </div>
        </details>
      </div>

      <div className="flex flex-wrap items-center justify-end gap-3 sticky bottom-0 bg-cream/90 backdrop-blur-sm py-4 -mx-5 px-5 border-t border-mist">
        <button onClick={() => save(false)} disabled={busy} data-testid="admin-editor-save-draft"
          className="inline-flex items-center gap-2 rounded-full border-2 border-mist px-5 py-2.5 text-sm font-semibold hover:border-brand-pink disabled:opacity-60">
          Taslak olarak kaydet
        </button>
        <button onClick={() => save(true)} disabled={busy} data-testid="admin-editor-publish"
          className="inline-flex items-center gap-2 rounded-full bg-ink text-cream px-6 py-2.5 text-sm font-semibold hover:bg-brand-pink-deep transition-colors disabled:opacity-60">
          <Save className="h-4 w-4" />
          {busy ? "Kaydediliyor…" : "Yayınla"}
        </button>
      </div>
    </div>
  );
}
